import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { ILegalPhaseFullObject } from '../../../Interfaces/ILegalPhase';
import { LegalPhaseRowEdit } from './LegalPhaseRowEdit';
import { LegalPhaseDialogEdit } from './LegalPhaseDialogEdit';
import { CopyFromTransactionType } from './CopyFromTransactionType';
import { getNewPhase } from '../../../core/generic_helper';
import { grey } from '@mui/material/colors';
import { useGetLegalTransactionTypes } from '../../../hooks/reactQuerys/LegalTransactionQueries';
import { IAdminstrativeUnit } from '../../../Interfaces/IAdminstrativeUnit';
import { IFederalState, ICourtCourtRole } from '../../../Interfaces/IAemter';
import { ILegalRequirementState } from '../../../Interfaces/ILegalRequirementState';

interface IProps {
  legalPhaseArray: ILegalPhaseFullObject[];
  setLegalPhaseArray: Function;
  setLegalPhaseFullObjectArray: Function;
  setIsOpenEditDialog: Function;
  adminUnitArray: IAdminstrativeUnit[];
  federalStateArray: IFederalState[];
  courtcourtroleArray: ICourtCourtRole[];
  legaltransactionId: number;
  legalRequirementStates: ILegalRequirementState[];
}

export const LegalPhaseTableEdit: React.FC<IProps> = ({
  legalPhaseArray,
  setLegalPhaseArray,
  setLegalPhaseFullObjectArray,
  setIsOpenEditDialog,
  adminUnitArray,
  legaltransactionId,
  courtcourtroleArray,
  federalStateArray,
  legalRequirementStates,
}) => {
  const [selectedRequirementsIds, setSelectedRequirementsIds] = useState<
    number[]
  >([]);
  const [editNewPhase, setEditNewPhase] = useState(
    getNewPhase(legalPhaseArray)
  );
  const [isOpenAddNewPhase, setIsOpenAddNewPhase] = useState(false);
  const [isOpenCopy, setIsOpenCopy] = useState(false);

  const { legalTransactionTypes, isLoadingLegalTransactionTypes } =
    useGetLegalTransactionTypes();

  const handleAddNew = () => {
    setEditNewPhase(getNewPhase(legalPhaseArray));
    setIsOpenAddNewPhase(true);
  };

  const handleSave = async () => {
    // Ausfiltern nicht vorhandener Conditions
    const saveLegalPhaseArray = legalPhaseArray.map(lg =>
      lg.PreconditionArray.length === 0
        ? lg
        : {
            ...lg,
            PreconditionArray: lg.PreconditionArray.filter(
              preCon =>
                legalPhaseArray.find(
                  preLg => preLg.idLegalPhase === preCon.idLegalPhase
                ) !== undefined
            ),
          }
    );
    setLegalPhaseFullObjectArray([...saveLegalPhaseArray]);
    setIsOpenEditDialog(false);
  };

  const deleteMultiFunction = () => {
    const newLegalPhaseArray = legalPhaseArray.map(legalPhase => ({
      ...legalPhase,
      LegalRequirementArray: legalPhase.LegalRequirementArray.filter(
        legalRequirement =>
          !selectedRequirementsIds.includes(legalRequirement.idLegalRequirement)
      ),
    }));

    setLegalPhaseArray(newLegalPhaseArray);
    setSelectedRequirementsIds([]);
  };

  if (isLoadingLegalTransactionTypes) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          height: '80vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!legalTransactionTypes) {
    return (
      <Alert severity='error'>Fehler beim Laden der Rechtsgeschäftsart</Alert>
    );
  }

  return (
    <>
      {isOpenAddNewPhase && (
        <LegalPhaseDialogEdit
          currentPhase={editNewPhase}
          currentPhaseEdit={editNewPhase}
          setCurrentPhaseEdit={setEditNewPhase}
          legalPhaseArray={legalPhaseArray}
          setLegalPhaseArray={setLegalPhaseArray}
          setIsOpenNew={setIsOpenAddNewPhase}
        />
      )}
      <Box
        sx={{
          position: 'sticky',
          top: 64,
          backgroundColor: grey[100],
          zIndex: 1000,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #ddd',
          padding: '16px 16px',
        }}
      >
        <Box
          sx={{
            visibility:
              selectedRequirementsIds.length > 0 ? 'visible' : 'hidden',
          }}
        >
          {selectedRequirementsIds.length > 0 && (
            <Button variant='outlined' onClick={deleteMultiFunction}>
              Löschen
            </Button>
          )}
        </Box>

        {/* Button-Gruppe, bleibt rechts ausgerichtet */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant='outlined' onClick={() => setIsOpenCopy(true)}>
            Aus Rechtsgeschäftsart kopieren
          </Button>
          <Button variant='outlined' onClick={handleAddNew}>
            Neue Phase
          </Button>
          <Button variant='contained' onClick={handleSave}>
            Übernehmen
          </Button>
        </Box>
      </Box>

      <Box sx={{ pl: 2, pr: 2, pb: 2, pt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={'5%'}></TableCell>
              <TableCell width={'25%'}>Bezeichnung</TableCell>
              <TableCell width={'15%'}>Vorgänger</TableCell>
              <TableCell width={'15%'}>Gemarkung</TableCell>
              <TableCell width={'10%'}></TableCell>
              <TableCell width={'5%'}>Interne Maßnahme</TableCell>
              <TableCell width={'5%'}>Upload Mandanten</TableCell>
              <TableCell width={'5%'}>Upload Mitarbeiter</TableCell>
              <TableCell width={'5%'}>
                Auto. <span style={{ whiteSpace: 'nowrap' }}>E-Mail</span>
              </TableCell>
              <TableCell width={'10%'}>Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {legalPhaseArray
              .sort((a, b) => (a.Position < b.Position ? -1 : 1))
              .map(x => (
                <LegalPhaseRowEdit
                  key={`idLegalPhase-${x.idLegalPhase}`}
                  currentPhase={x}
                  legalPhaseArray={legalPhaseArray}
                  setLegalPhaseArray={setLegalPhaseArray}
                  adminUnitArray={adminUnitArray}
                  federalStateArray={federalStateArray}
                  courtcourtroleArray={courtcourtroleArray}
                  legaltransactionId={legaltransactionId}
                  selectedRequirementsIds={selectedRequirementsIds}
                  setSelectedRequirementsIds={setSelectedRequirementsIds}
                  legalRequirementStates={legalRequirementStates}
                />
              ))}
          </TableBody>
        </Table>
      </Box>

      <CopyFromTransactionType
        transactionTypes={legalTransactionTypes}
        open={isOpenCopy}
        onClose={() => setIsOpenCopy(false)}
        legalPhaseArray={legalPhaseArray}
        setLegalPhaseArray={setLegalPhaseArray}
        idLegalTransaction={legaltransactionId}
      />
    </>
  );
};
